import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import purchaseOrderService from '../../services/purchaseOrderService';
import paymentService from '../../services/paymentService';
import './PurchaseOrders.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PurchaseOrders = () => {
  const { user } = useContext(AuthContext);
  const subsidiary = localStorage.getItem('selectedSubsidiaryKey') || 'LH';

  // Permission checks
  const checkPermission = (permissionName) => {
    if (!user?.permissions || !Array.isArray(user.permissions)) return false;

    return user.permissions.some(p => {
      // Handle permission objects with 'name' property
      if (typeof p === 'object' && p.name) {
        return p.name === permissionName;
      }
      // Handle plain string permissions
      if (typeof p === 'string') {
        return p === permissionName;
      }
      return false;
    });
  };

  const hasCreatePermission = checkPermission('purchaseorders.create');
  const hasUpdatePermission = checkPermission('purchaseorders.update');
  const hasDeletePermission = checkPermission('purchaseorders.delete');
  const hasReadPermission = checkPermission('purchaseorders.read');
  const hasPayPermission = checkPermission('purchaseorders.pay');
  const hasPayAdminPermission = checkPermission('purchaseorders.payadmin');

  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [modalItems, setModalItems] = useState([]);
  const [stockLocations, setStockLocations] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    order_state: '',
    order_date: '',
    vendor_id: '',
    item_id: '',
    subsidiary: subsidiary
  });

  const [showModal, setShowModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const [orderForm, setOrderForm] = useState({
    name: '',
    order_date: new Date().toISOString().split('T')[0],
    order_state: 'Fulfilled',
    order_amount: '',
    amount_paid: '',
    vendor_id: '',
    vendor_name: '',
    subsidiary: subsidiary,
    payment_type_id: '',
    stock_location_id: ''
  });

  const [orderLines, setOrderLines] = useState([{
    item_id: '',
    qty: '',
    unit_price: '',
    total_price: '',
    isDirty: false
  }]);

  // Autocomplete state for vendor in form
  const [vendorInput, setVendorInput] = useState('');
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);

  // Autocomplete state for items in order lines
  const [itemInputs, setItemInputs] = useState({});
  const [filteredItems, setFilteredItems] = useState({});
  const [showItemDropdown, setShowItemDropdown] = useState({});

  // Autocomplete state for vendor in search form
  const [searchVendorInput, setSearchVendorInput] = useState('');
  const [filteredSearchVendors, setFilteredSearchVendors] = useState([]);
  const [showSearchVendorDropdown, setShowSearchVendorDropdown] = useState(false);

  // Autocomplete state for item in search form
  const [searchItemInput, setSearchItemInput] = useState('');
  const [filteredSearchItems, setFilteredSearchItems] = useState([]);
  const [showSearchItemDropdown, setShowSearchItemDropdown] = useState(false);

  // Record History modal state
  const [historyItem, setHistoryItem] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const handleOpenHistory = async (order) => {
    setHistoryItem(order);
    setHistoryData(null);
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API_URL}/purchase-orders/${order.id}/history`, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });
      setHistoryData(res.data);
    } catch (err) {
      setHistoryData({ error: err.response?.data?.error || err.message });
    } finally {
      setHistoryLoading(false);
    }
  };

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
  const [isPaymentReadOnly, setIsPaymentReadOnly] = useState(false);
  const [paymentNames, setPaymentNames] = useState([]);
  const [paymentLines, setPaymentLines] = useState([]);
  const [paymentForm, setPaymentForm] = useState({
    payment_id: '',
    payment_date: '',
    payment_amount: '',
    balance_amount: ''
  });

  const orderStates = ['Draft', 'Entered', 'Fulfilled', 'Forecast', 'Paid', 'Unpaid', 'Payment Initiated'];

  useEffect(() => {
    fetchData();
  }, [filters, searchTerm]);

  useEffect(() => {
    fetchVendors();
    fetchItems();
    fetchPaymentTypes();
    fetchStockLocations();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const ordersRes = await purchaseOrderService.getAllOrders({
        ...filters,
        order_no: searchTerm  // Search by order number
      });

      if (ordersRes.success) {
        setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
      }
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const vendorsRes = await purchaseOrderService.getVendors();
      if (vendorsRes.success) {
        setVendors(Array.isArray(vendorsRes.data) ? vendorsRes.data : []);
      }
    } catch (err) {
      console.error('Error fetching vendors:', err);
    }
  };

  const fetchItems = async () => {
    try {
      const itemsRes = await purchaseOrderService.getItems(subsidiary);
      if (itemsRes.success) {
        setItems(Array.isArray(itemsRes.data) ? itemsRes.data : []);
      }
    } catch (err) {
      console.error('Error fetching items:', err);
    }
  };

  const fetchItemsForLocation = async (locationId) => {
    try {
      const res = await purchaseOrderService.getItemsForLocation(subsidiary, locationId);
      if (res.success) setModalItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching items for location:', err);
    }
  };

  const fetchPaymentTypes = async () => {
    try {
      const typesRes = await purchaseOrderService.getPaymentTypes();
      if (typesRes.success) {
        setPaymentTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
      }
    } catch (err) {
      console.error('Error fetching payment types:', err);
    }
  };

  const fetchStockLocations = async () => {
    try {
      const res = await purchaseOrderService.getStockLocations();
      if (res.success) setStockLocations(res.data || []);
    } catch (err) {
      console.error('Error fetching stock locations:', err);
    }
  };

  // After item selection in a line, suggest stock_location_id if not already set
  const suggestStockLocation = async (updatedLines) => {
    const itemIds = updatedLines.map(l => l.item_id).filter(Boolean);
    if (!itemIds.length) return;
    try {
      const res = await purchaseOrderService.getSuggestedLocation(itemIds);
      if (res.success && res.data.suggested_location_id) {
        setOrderForm(prev => ({
          ...prev,
          stock_location_id: prev.stock_location_id || res.data.suggested_location_id
        }));
      }
    } catch (err) {
      console.error('Error fetching suggested location:', err);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSearchVendorInput('');
    setSearchItemInput('');
    setFilters({
      order_state: 'Unpaid',
      order_date: '',
      vendor_id: '',
      item_id: '',
      subsidiary: subsidiary
    });
  };

  const handleOrderFormChange = (e) => {
    const { name, value } = e.target;
    setOrderForm(prev => ({ ...prev, [name]: value }));
    if (name === 'stock_location_id') {
      fetchItemsForLocation(value);
      const newLines = orderLines.map(l => ({ ...l, item_id: '', isDirty: false }));
      setOrderLines(newLines);
      setItemInputs({});
      setFilteredItems({});
      setShowItemDropdown({});
    }
  };

  // Vendor autocomplete for form
  const handleVendorInputChange = (e) => {
    const value = e.target.value;
    setVendorInput(value);

    if (value.trim() === '') {
      setFilteredVendors([]);
      setShowVendorDropdown(false);
      setOrderForm(prev => ({ ...prev, vendor_id: '', vendor_name: '' }));
    } else {
      const filtered = vendors.filter(vendor =>
        vendor.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredVendors(filtered);
      setShowVendorDropdown(true);
    }
  };

  const handleVendorSelect = (vendor) => {
    setVendorInput(vendor.name);
    setOrderForm(prev => ({ ...prev, vendor_id: vendor.id, vendor_name: vendor.name }));
    setShowVendorDropdown(false);
    setFilteredVendors([]);
  };

  // Vendor autocomplete for search
  const handleSearchVendorInputChange = (e) => {
    const value = e.target.value;
    setSearchVendorInput(value);

    if (value.trim() === '') {
      setFilteredSearchVendors([]);
      setShowSearchVendorDropdown(false);
      setFilters(prev => ({ ...prev, vendor_id: '' }));
    } else {
      const filtered = vendors.filter(vendor =>
        vendor.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSearchVendors(filtered);
      setShowSearchVendorDropdown(true);
    }
  };

  const handleSearchVendorSelect = (vendor) => {
    setSearchVendorInput(vendor.name);
    setFilters(prev => ({ ...prev, vendor_id: vendor.id }));
    setShowSearchVendorDropdown(false);
    setFilteredSearchVendors([]);
  };

  // Item autocomplete for search
  const handleSearchItemInputChange = (e) => {
    const value = e.target.value;
    setSearchItemInput(value);

    if (value.trim() === '') {
      setFilteredSearchItems([]);
      setShowSearchItemDropdown(false);
      setFilters(prev => ({ ...prev, item_id: '' }));
    } else {
      const filtered = items.filter(item =>
        item.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSearchItems(filtered);
      setShowSearchItemDropdown(true);
    }
  };

  const handleSearchItemSelect = (item) => {
    setSearchItemInput(item.name);
    setFilters(prev => ({ ...prev, item_id: item.id }));
    setShowSearchItemDropdown(false);
    setFilteredSearchItems([]);
  };

  // Item autocomplete for order lines
  const handleItemInputChange = (index, value) => {
    // Update the input value for this line
    setItemInputs(prev => ({ ...prev, [index]: value }));

    if (value.trim() === '') {
      setFilteredItems(prev => ({ ...prev, [index]: [] }));
      setShowItemDropdown(prev => ({ ...prev, [index]: false }));

      // Clear item selection
      const newLines = [...orderLines];
      newLines[index].item_id = '';
      newLines[index].isDirty = true;
      setOrderLines(newLines);
    } else {
      // Filter items by location (or all items if no location selected)
      const filtered = modalItems.filter(item =>
        item.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredItems(prev => ({ ...prev, [index]: filtered }));
      setShowItemDropdown(prev => ({ ...prev, [index]: true }));
    }
  };

  const handleItemSelect = (index, item) => {
    setItemInputs(prev => ({ ...prev, [index]: item.name }));
    const newLines = [...orderLines];
    newLines[index].item_id = item.id;
    newLines[index].isDirty = true;
    setOrderLines(newLines);
    setShowItemDropdown(prev => ({ ...prev, [index]: false }));
    setFilteredItems(prev => ({ ...prev, [index]: [] }));
    suggestStockLocation(newLines);
  };

  const handleOrderLineChange = (index, field, value) => {
    const newLines = [...orderLines];
    newLines[index][field] = value;

    // Mark line as dirty when any field is changed
    newLines[index].isDirty = true;

    // Auto-calculate total price
    if (field === 'qty' || field === 'unit_price') {
      const qty = parseFloat(newLines[index].qty) || 0;
      const unitPrice = parseFloat(newLines[index].unit_price) || 0;
      newLines[index].total_price = (qty * unitPrice).toFixed(2);
    }

    setOrderLines(newLines);
  };

  const addOrderLine = () => {
    const newIndex = orderLines.length;
    setOrderLines([...orderLines, {
      item_id: '',
      qty: '',
      unit_price: '',
      total_price: '',
      isDirty: false
    }]);
    // Initialize empty item input for new line
    setItemInputs(prev => ({ ...prev, [newIndex]: '' }));
  };

  const removeOrderLine = (index) => {
    const newLines = orderLines.filter((_, i) => i !== index);
    setOrderLines(newLines.length > 0 ? newLines : [{
      item_id: '',
      qty: '',
      unit_price: '',
      total_price: '',
      isDirty: false
    }]);

    // Clean up item input state for removed line
    setItemInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[index];
      // Re-index remaining inputs
      const reindexed = {};
      Object.keys(newInputs).forEach(key => {
        const oldIndex = parseInt(key);
        if (oldIndex > index) {
          reindexed[oldIndex - 1] = newInputs[key];
        } else {
          reindexed[key] = newInputs[key];
        }
      });
      return reindexed;
    });

    // Clean up dropdown states
    setFilteredItems(prev => {
      const newFiltered = { ...prev };
      delete newFiltered[index];
      return newFiltered;
    });
    setShowItemDropdown(prev => {
      const newShow = { ...prev };
      delete newShow[index];
      return newShow;
    });
  };

  const handleCreateNew = () => {
    setEditingOrder(null);
    setIsReadOnly(false);
    setOrderForm({
      name: '',
      order_date: new Date().toISOString().split('T')[0],
      order_state: 'Fulfilled',
      order_amount: '',
      amount_paid: '',
      vendor_id: '',
      vendor_name: '',
      subsidiary: subsidiary,
      payment_type_id: '',
      stock_location_id: ''
    });
    setOrderLines([
      { item_id: '', qty: '', unit_price: '', total_price: '', isDirty: false },
      { item_id: '', qty: '', unit_price: '', total_price: '', isDirty: false },
      { item_id: '', qty: '', unit_price: '', total_price: '', isDirty: false },
      { item_id: '', qty: '', unit_price: '', total_price: '', isDirty: false }
    ]);
    setVendorInput('');
    // Initialize empty item inputs for new order
    setItemInputs({ 0: '', 1: '', 2: '', 3: '' });
    setFilteredItems({});
    setShowItemDropdown({});
    fetchItemsForLocation('');
    setShowModal(true);
  };

  const handleEdit = async (order, readOnly = false) => {
    setEditingOrder(order);
    setIsReadOnly(readOnly);
    fetchItemsForLocation(order.stock_location_id || '');

    // Fix date conversion
    let formattedDate = '';
    if (order.order_date) {
      const date = new Date(order.order_date);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      formattedDate = `${year}-${month}-${day}`;
    }

    setOrderForm({
      ...order,
      order_date: formattedDate
    });

    setVendorInput(order.vendor_name || '');

    // Load order lines
    try {
      const linesRes = await purchaseOrderService.getOrderLines(order.id);
      if (linesRes.success && Array.isArray(linesRes.data) && linesRes.data.length > 0) {
        setOrderLines(linesRes.data.map(line => ({
          id: line.id,
          item_id: line.item_id,
          qty: line.qty,
          unit_price: line.unit_price,
          total_price: line.total_price,
          isDirty: false // Existing lines from DB are not dirty initially
        })));

        // Initialize item inputs for existing lines (name comes from the order lines response)
        const inputs = {};
        linesRes.data.forEach((line, index) => {
          inputs[index] = line.name || '';
        });
        setItemInputs(inputs);
      } else {
        setOrderLines([{
          item_id: '',
          qty: '',
          unit_price: '',
          total_price: '',
          isDirty: false
        }]);
        setItemInputs({ 0: '' });
      }
    } catch (err) {
      console.error('Error loading order lines:', err);
    }

    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validate vendor selection
    if (!orderForm.vendor_id) {
      setError('Please select a valid vendor from the list');
      return;
    }

    // Different logic for CREATE vs UPDATE
    let linesToSend;

    if (editingOrder) {
      // UPDATE: Send ALL lines (both dirty and non-dirty) to preserve existing lines
      // Filter out empty lines (lines without item_id)
      linesToSend = orderLines.filter(line => line.item_id && line.qty && line.unit_price);

      // Allow empty lines array for UPDATE (user might want to delete all lines)
      // Backend will handle this and set order_amount to 0
    } else {
      // CREATE: Only send dirty (modified) lines
      const dirtyLines = orderLines.filter(line => line.isDirty);

      // Validate dirty lines - only dirty lines need complete data
      const invalidDirtyLines = dirtyLines.filter(line => !line.item_id || !line.qty || !line.unit_price);
      if (invalidDirtyLines.length > 0) {
        setError('Please complete all fields for modified order lines or remove incomplete lines');
        return;
      }

      // Valid lines are those that are dirty and have all required fields
      linesToSend = dirtyLines.filter(line => line.item_id && line.qty && line.unit_price);

      if (linesToSend.length === 0) {
        setError('Please add at least one order line with complete data');
        return;
      }
    }

    try {
      // Calculate order amount from lines to send
      const orderAmount = linesToSend.reduce((sum, line) => sum + parseFloat(line.total_price || 0), 0);

      // Clean up lines - remove isDirty flag before sending to backend
      const cleanedLines = linesToSend.map(line => {
        const { isDirty, ...cleanLine } = line;
        return cleanLine;
      });

      const orderData = {
        ...orderForm,
        order_amount: orderAmount,
        lines: cleanedLines
      };

      let response;
      if (editingOrder) {
        response = await purchaseOrderService.updateOrder(editingOrder.id, orderData);
      } else {
        response = await purchaseOrderService.createOrder(orderData);
      }

      if (response.success) {
        setSuccessMessage(editingOrder ? 'Order updated successfully!' : 'Order created successfully!');
        setShowModal(false);
        setEditingOrder(null);
        resetForm();
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Save order error:', err);
      setError(err.error || err.message || 'Failed to save order');
    }
  };

  const handleDelete = async (order) => {
    if (!window.confirm(`Are you sure you want to delete order "${order.order_no}"?`)) {
      return;
    }

    try {
      const response = await purchaseOrderService.deleteOrder(order.id);
      if (response.success) {
        setSuccessMessage('Order deleted successfully!');
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Delete order error:', err);
      setError(err.error || err.message || 'Failed to delete order');
    }
  };

  // Payment Modal Functions
  const handlePaymentDetails = async (order) => {
    setSelectedOrderForPayment(order);
    setShowPaymentModal(true);
    setError('');
    setSuccessMessage('');

    // Determine if modal should be read-only
    // Read-only if order is Paid AND user has pay permission but NOT payadmin permission
    const shouldBeReadOnly = order.order_state === 'Paid' && hasPayPermission && !hasPayAdminPermission;
    setIsPaymentReadOnly(shouldBeReadOnly);

    // Fetch payment-related data
    try {
      const [paymentNamesRes, paymentLinesRes] = await Promise.all([
        paymentService.getPaymentNames(),
        paymentService.getPaymentLinesByTransaction(order.id, 'PO')
      ]);

      if (paymentNamesRes.success) {
        setPaymentNames(paymentNamesRes.data);
      }
      if (paymentLinesRes.success) {
        setPaymentLines(paymentLinesRes.data);
      }
    } catch (err) {
      console.error('Error fetching payment data:', err);
      setError('Failed to load payment data');
    }
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedOrderForPayment(null);
    setPaymentForm({
      payment_id: '',
      payment_date: '',
      payment_amount: '',
      balance_amount: ''
    });
    setPaymentLines([]);
    setPaymentNames([]);
  };

  const handlePaymentFormChange = async (e) => {
    const { name, value } = e.target;
    setPaymentForm(prev => ({ ...prev, [name]: value }));

    // Fetch payment details when payment is selected
    if (name === 'payment_id' && value) {
      try {
        const res = await paymentService.getPaymentDetails(value);
        if (res.success) {
          setPaymentForm(prev => ({
            ...prev,
            payment_date: res.data.payment_date ? new Date(res.data.payment_date).toISOString().split('T')[0] : '',
            balance_amount: res.data.balance_amount || '',
            payment_amount: selectedOrderForPayment?.order_amount || ''
          }));
        }
      } catch (err) {
        console.error('Error fetching payment details:', err);
      }
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const paymentData = {
        payment_id: paymentForm.payment_id,
        payment_type: 'PO',
        type_transaction_id: selectedOrderForPayment.id,
        payment_amount: paymentForm.payment_amount,
        subsidiary: subsidiary
      };

      const response = await paymentService.createPaymentLine(paymentData);
      if (response.success) {
        setSuccessMessage('Payment line added successfully!');
        // Refresh payment lines
        const paymentLinesRes = await paymentService.getPaymentLinesByTransaction(selectedOrderForPayment.id, 'PO');
        if (paymentLinesRes.success) {
          setPaymentLines(paymentLinesRes.data);
        }
        // Refresh payment names to update balances
        const paymentNamesRes = await paymentService.getPaymentNames();
        if (paymentNamesRes.success) {
          setPaymentNames(paymentNamesRes.data);
        }
        // Reset form
        setPaymentForm({
          payment_id: '',
          payment_date: '',
          payment_amount: '',
          balance_amount: ''
        });
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to add payment line');
      }
    } catch (err) {
      console.error('Payment submit error:', err);
      setError(err.error || err.message || 'Failed to add payment line');
    }
  };

  const handleDeletePaymentLine = async (line) => {
    if (!window.confirm('Are you sure you want to delete this payment line?')) {
      return;
    }

    try {
      const response = await paymentService.deletePaymentLine(line.id, line.payment_id, line.payment_amount);
      if (response.success) {
        setSuccessMessage('Payment line deleted successfully!');
        // Refresh payment lines
        const paymentLinesRes = await paymentService.getPaymentLinesByTransaction(selectedOrderForPayment.id, 'PO');
        if (paymentLinesRes.success) {
          setPaymentLines(paymentLinesRes.data);
        }
        // Refresh payment names to update balances
        const paymentNamesRes = await paymentService.getPaymentNames();
        if (paymentNamesRes.success) {
          setPaymentNames(paymentNamesRes.data);
        }
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to delete payment line');
      }
    } catch (err) {
      console.error('Delete payment line error:', err);
      setError(err.error || err.message || 'Failed to delete payment line');
    }
  };

  const handleMarkAsPaid = async (order) => {
    // Check if payment type is selected
    if (!orderForm.payment_type_id) {
      setError('Please select a payment type before marking as paid');
      return;
    }

    try {
      const payload = {
        ...orderForm,
        amount_paid: 'Y',
        order_state: 'Paid'
      };

      const response = await purchaseOrderService.updateOrder(order.id, payload);
      if (response.success) {
        setSuccessMessage('Order marked as paid successfully!');
        setShowModal(false);
        setEditingOrder(null);
        resetForm();
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Mark as paid error:', err);
      setError(err.error || err.message || 'Failed to mark order as paid');
    }
  };


  const resetForm = () => {
    setOrderForm({
      name: '',
      order_date: new Date().toISOString().split('T')[0],
      order_state: 'Fulfilled',
      order_amount: '',
      amount_paid: '',
      vendor_id: '',
      vendor_name: '',
      subsidiary: subsidiary,
      payment_type_id: '',
      stock_location_id: ''
    });
    setOrderLines([
      { item_id: '', qty: '', unit_price: '', total_price: '', isDirty: false },
      { item_id: '', qty: '', unit_price: '', total_price: '', isDirty: false },
      { item_id: '', qty: '', unit_price: '', total_price: '', isDirty: false },
      { item_id: '', qty: '', unit_price: '', total_price: '', isDirty: false }
    ]);
    setVendorInput('');
  };

  const filteredOrders = orders;

  return (
    <div className="purchase-orders-management">
      <div className="section-header">
        <div>
          <h2>Purchase Order Management</h2>
          <p>Manage purchase orders and payments</p>
        </div>
        {hasCreatePermission && (
          <button className="btn-create" onClick={handleCreateNew}>
            + Create Order
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Search and Filters */}
      <div className="search-filter-container">
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search by order number or name..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <span className="search-icon">🔍</span>
        </div>

        <div className="filters-row">
          <div style={{ position: 'relative', minWidth: '200px' }}>
            <input
              type="text"
              className="filter-input"
              placeholder="Filter by vendor"
              value={searchVendorInput}
              onChange={handleSearchVendorInputChange}
              onFocus={() => {
                if (searchVendorInput && filteredSearchVendors.length > 0) {
                  setShowSearchVendorDropdown(true);
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowSearchVendorDropdown(false), 200);
              }}
              autoComplete="off"
            />
            {showSearchVendorDropdown && filteredSearchVendors.length > 0 && (
              <div className="autocomplete-dropdown">
                {filteredSearchVendors.map((vendor) => (
                  <div
                    key={vendor.id}
                    onClick={() => handleSearchVendorSelect(vendor)}
                    className="autocomplete-item"
                  >
                    {vendor.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ position: 'relative', minWidth: '200px' }}>
            <input
              type="text"
              className="filter-input"
              placeholder="Filter by item"
              value={searchItemInput}
              onChange={handleSearchItemInputChange}
              onFocus={() => {
                if (searchItemInput && filteredSearchItems.length > 0) {
                  setShowSearchItemDropdown(true);
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowSearchItemDropdown(false), 200);
              }}
              autoComplete="off"
            />
            {showSearchItemDropdown && filteredSearchItems.length > 0 && (
              <div className="autocomplete-dropdown">
                {filteredSearchItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => handleSearchItemSelect(item)}
                    className="autocomplete-item"
                  >
                    {item.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          <select
            name="order_state"
            className="filter-select"
            value={filters.order_state}
            onChange={handleFilterChange}
          >
            <option value="">All States</option>
            {orderStates.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>

          <input
            type="date"
            name="order_date"
            className="filter-input"
            value={filters.order_date}
            onChange={handleFilterChange}
          />

          <button className="btn-reset" onClick={resetFilters}>
            Reset
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading purchase orders...</div>
      ) : (
        <>
          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order Number</th>
                  <th>Name</th>
                  <th>Vendor</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>State</th>
                  <th>Paid</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="no-data">No purchase orders found</td>
                  </tr>
                ) : (
                  filteredOrders.map(order => (
                    <tr key={order.id}>
                      <td><strong>{order.order_no || '-'}</strong></td>
                      <td>{order.name || '-'}</td>
                      <td>{order.vendor_name || '-'}</td>
                      <td>{order.order_date ? new Date(order.order_date).toLocaleDateString() : '-'}</td>
                      <td>₹{order.order_amount || '0.00'}</td>
                      <td>
                        <span className={`state-badge state-${order.order_state?.toLowerCase()}`}>
                          {order.order_state}
                        </span>
                      </td>
                      <td>{order.amount_paid === 'Y' ? 'Yes' : 'No'}</td>
                      <td>
                        <div className="action-buttons">
                          {/* Edit icon: For non-Paid orders with update permission OR Paid orders with payadmin permission */}
                          {((order.order_state !== 'Paid' && hasUpdatePermission) || (order.order_state === 'Paid' && hasPayAdminPermission)) && (
                            <button
                              className="btn-icon btn-edit"
                              onClick={() => handleEdit(order, false)}
                              title="Edit Order"
                            >
                              ✏️
                            </button>
                          )}

                          {/* View icon: Show when user only has read permission and no edit icon is shown */}
                          {hasReadPermission && !((order.order_state !== 'Paid' && hasUpdatePermission) || (order.order_state === 'Paid' && hasPayAdminPermission)) && (
                            <button
                              className="btn-icon btn-view"
                              onClick={() => handleEdit(order, true)}
                              title="View Order Details"
                            >
                              👁️
                            </button>
                          )}

                          {/* Delete icon: For non-Paid orders with delete permission OR Paid orders with payadmin permission */}
                          {((order.order_state !== 'Paid' && hasDeletePermission) || (order.order_state === 'Paid' && hasPayAdminPermission)) && (
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDelete(order)}
                              title="Delete Order"
                            >
                              🗑️
                            </button>
                          )}
                          {(hasPayPermission || hasPayAdminPermission) && (
                            <button
                              className="btn-icon btn-pay"
                              onClick={() => handlePaymentDetails(order)}
                              title="Payment Details"
                            >
                              ₹
                            </button>
                          )}
                          <button
                            className="btn-icon"
                            onClick={() => handleOpenHistory(order)}
                            title="Record History"
                            style={{ fontSize: '0.75rem', fontWeight: 600, color: '#555' }}
                          >
                            🕓
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Create/Edit Order Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isReadOnly ? 'View Order Details' : (editingOrder ? 'Edit Purchase Order' : 'Create New Purchase Order')}</h3>
              <button className="close-button" onClick={() => { setShowModal(false); setEditingOrder(null); setIsReadOnly(false); resetForm(); }}>×</button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 60px)' }}>
              {error && <div className="error-message" style={{ margin: '0 20px 10px 20px' }}>{error}</div>}

              <div className="modal-body">
                {/* Order Header Section */}
                <div className="form-section">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={orderForm.name}
                        onChange={handleOrderFormChange}
                        placeholder="Order Name"
                        required
                        disabled={isReadOnly}
                      />
                    </div>

                    <div className="form-group date-field">
                      <label>Order Date *</label>
                      <input
                        type="date"
                        name="order_date"
                        value={orderForm.order_date}
                        onChange={handleOrderFormChange}
                        required
                        disabled={isReadOnly}
                      />
                    </div>

                    <div className="form-group">
                      <label>Order State *</label>
                      <select
                        name="order_state"
                        value={orderForm.order_state}
                        onChange={handleOrderFormChange}
                        required
                        disabled={isReadOnly}
                      >
                        <option value="Draft">Draft</option>
                        <option value="Entered">Entered</option>
                        <option value="Fulfilled">Fulfilled</option>
                        <option value="Forecast">Forecast</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Stock Location</label>
                      <select
                        name="stock_location_id"
                        value={orderForm.stock_location_id || ''}
                        onChange={handleOrderFormChange}
                        disabled={isReadOnly || orderLines.some(l => l.item_id)}
                      >
                        <option value="">-- None --</option>
                        {stockLocations.map(loc => (
                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Vendor *</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          value={vendorInput}
                          onChange={handleVendorInputChange}
                          onFocus={() => {
                            if (vendorInput && filteredVendors.length > 0 && !isReadOnly) {
                              setShowVendorDropdown(true);
                            }
                          }}
                          onBlur={() => {
                            setTimeout(() => setShowVendorDropdown(false), 200);
                          }}
                          placeholder="Enter Vendor Name"
                          required
                          autoComplete="off"
                          disabled={isReadOnly}
                        />
                        {showVendorDropdown && filteredVendors.length > 0 && (
                          <div className="autocomplete-dropdown">
                            {filteredVendors.map((vendor) => (
                              <div
                                key={vendor.id}
                                onClick={() => handleVendorSelect(vendor)}
                                className="autocomplete-item"
                              >
                                {vendor.name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Order Amount</label>
                      <input
                        type="number"
                        name="order_amount"
                        value={orderForm.order_amount}
                        onChange={handleOrderFormChange}
                        placeholder="Order Amount"
                        readOnly
                        className="readonly-field"
                      />
                    </div>

                    <div className="form-group">
                      <label>Order Number</label>
                      <input
                        type="text"
                        name="order_no"
                        value={orderForm.order_no || ''}
                        placeholder="Auto-generated"
                        readOnly
                        className="readonly-field"
                      />
                    </div>

                    {editingOrder && ((hasPayPermission && orderForm.order_state !== 'Paid') || user?.role === 'SADM') && (
                      <>
                        <div className="form-group">
                          <label>Payment Type</label>
                          <select
                            name="payment_type_id"
                            value={orderForm.payment_type_id}
                            onChange={handleOrderFormChange}
                            disabled={isReadOnly}
                          >
                            <option value="">Select Payment Type</option>
                            {paymentTypes.map((type) => (
                              <option key={type.id} value={type.id}>
                                {type.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                </div>

              {/* Order Lines Section */}
              <div className="form-section">
                <div className="section-title-row">
                  <h4 className="section-title">Order Lines</h4>
                  {!isReadOnly && (
                    <button type="button" className="btn-add-line" onClick={addOrderLine}>
                      + Add Line
                    </button>
                  )}
                </div>

                <div className="order-lines-container">
                  {/* Header Row */}
                  <div className="order-line-header">
                    <div className="line-header-cell">Item *</div>
                    <div className="line-header-cell line-field-small">Quantity *</div>
                    <div className="line-header-cell line-field-small">Unit Price *</div>
                    <div className="line-header-cell line-field-small">Total</div>
                    <div className="line-header-cell line-actions"></div>
                  </div>

                  {/* Data Rows */}
                  {orderLines.map((line, index) => (
                    <div key={index} className="order-line-row">
                      <div className="line-field" style={{ position: 'relative' }}>
                        <input
                          type="text"
                          value={itemInputs[index] || ''}
                          onChange={(e) => handleItemInputChange(index, e.target.value)}
                          onFocus={() => {
                            if (itemInputs[index] && filteredItems[index]?.length > 0 && !isReadOnly) {
                              setShowItemDropdown(prev => ({ ...prev, [index]: true }));
                            }
                          }}
                          onBlur={() => {
                            setTimeout(() => {
                              setShowItemDropdown(prev => ({ ...prev, [index]: false }));
                            }, 200);
                          }}
                          placeholder="Enter Item Name"
                          autoComplete="off"
                          disabled={isReadOnly}
                        />
                        {showItemDropdown[index] && filteredItems[index]?.length > 0 && (
                          <div className="autocomplete-dropdown">
                            {filteredItems[index].map((item) => (
                              <div
                                key={item.id}
                                onClick={() => handleItemSelect(index, item)}
                                className="autocomplete-item"
                              >
                                {item.name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="line-field line-field-small">
                        <input
                          type="number"
                          value={line.qty}
                          onChange={(e) => handleOrderLineChange(index, 'qty', e.target.value)}
                          placeholder="0"
                          step="0.01"
                          disabled={isReadOnly}
                        />
                      </div>

                      <div className="line-field line-field-small">
                        <input
                          type="number"
                          value={line.unit_price}
                          onChange={(e) => handleOrderLineChange(index, 'unit_price', e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          disabled={isReadOnly}
                        />
                      </div>

                      <div className="line-field line-field-small">
                        <input
                          type="number"
                          value={line.total_price}
                          readOnly
                          className="readonly-field"
                        />
                      </div>

                      {!isReadOnly && (
                        <div className="line-actions">
                          <button
                            type="button"
                            className="btn-remove-line"
                            onClick={() => removeOrderLine(index)}
                            title="Remove Line"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="order-total">
                  <strong>Order Total: ₹{orderLines.reduce((sum, line) => sum + (parseFloat(line.total_price) || 0), 0).toFixed(2)}</strong>
                </div>
              </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); setEditingOrder(null); setIsReadOnly(false); resetForm(); }}>
                  {isReadOnly ? 'Close' : 'Cancel'}
                </button>
                {!isReadOnly && (
                  <>
                    <button type="submit" className="btn-submit">
                      {editingOrder ? 'Update Order' : 'Create Order'}
                    </button>
                    {editingOrder && ((hasPayPermission && orderForm.order_state !== 'Paid') || user?.role === 'SADM') && (
                      <button
                        type="button"
                        className="btn-submit"
                        onClick={() => handleMarkAsPaid(editingOrder)}
                        disabled={!orderForm.payment_type_id}
                        style={{ background: '#28a745' }}
                      >
                        Mark as Paid
                      </button>
                    )}
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Details Modal */}
      {showPaymentModal && selectedOrderForPayment && (
        <div className="modal-overlay" onClick={handleClosePaymentModal}>
          <div className="modal-content order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Payment Details - Order #{selectedOrderForPayment.name} {isPaymentReadOnly && '(View Only)'}</h3>
              <button className="close-button" onClick={handleClosePaymentModal}>×</button>
            </div>

            <form onSubmit={handlePaymentSubmit} style={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 60px)' }}>
              {error && <div className="error-message" style={{ margin: '0 20px 10px 20px' }}>{error}</div>}
              {successMessage && <div className="success-message" style={{ margin: '0 20px 10px 20px' }}>{successMessage}</div>}

              <div className="modal-body">
                {/* Order Summary Section */}
                <div className="form-section">
                  <h4 style={{ marginBottom: '15px', color: '#2d3748', borderBottom: '2px solid #F57C00', paddingBottom: '8px' }}>
                    Order Summary
                  </h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Order Amount</label>
                      <input
                        type="text"
                        value={`₹${parseFloat(selectedOrderForPayment.order_amount || 0).toFixed(2)}`}
                        disabled
                        style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                      />
                    </div>
                    <div className="form-group">
                      <label>Balance Amount</label>
                      <input
                        type="text"
                        value={`₹${parseFloat(paymentForm.balance_amount || selectedOrderForPayment.order_amount || 0).toFixed(2)}`}
                        disabled
                        style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Payment Form Section - Only show if not read-only */}
                {!isPaymentReadOnly && (
                  <div className="form-section">
                    <h4 style={{ marginBottom: '15px', color: '#2d3748', borderBottom: '2px solid #F57C00', paddingBottom: '8px' }}>
                      Add Payment
                    </h4>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Payment *</label>
                        <select
                          name="payment_id"
                          value={paymentForm.payment_id}
                          onChange={handlePaymentFormChange}
                          required
                        >
                          <option value="">Select Payment</option>
                          {paymentNames.map((payment) => (
                            <option key={payment.id} value={payment.id}>
                              {payment.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group date-field">
                        <label>Payment Date *</label>
                        <input
                          type="date"
                          name="payment_date"
                          value={paymentForm.payment_date}
                          onChange={handlePaymentFormChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Payment Amount *</label>
                        <input
                          type="number"
                          step="0.01"
                          name="payment_amount"
                          value={paymentForm.payment_amount}
                          onChange={handlePaymentFormChange}
                          placeholder="Enter amount"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Lines Table */}
                <div className="form-section">
                  <h4 style={{ marginBottom: '15px', color: '#2d3748', borderBottom: '2px solid #F57C00', paddingBottom: '8px' }}>
                    Payment History
                  </h4>
                  {paymentLines.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '12px 15px', textAlign: 'left' }}>Payment</th>
                            <th style={{ padding: '12px 15px', textAlign: 'left' }}>Date</th>
                            <th style={{ padding: '12px 15px', textAlign: 'right' }}>Amount</th>
                            <th style={{ padding: '12px 15px', textAlign: 'center' }}>Type</th>
                            {!isPaymentReadOnly && (
                              <th style={{ padding: '12px 15px', textAlign: 'center' }}>Actions</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {paymentLines.map((line) => (
                            <tr key={line.id}>
                              <td style={{ padding: '12px 15px' }}>{line.name}</td>
                              <td style={{ padding: '12px 15px' }}>{new Date(line.payment_date).toLocaleDateString()}</td>
                              <td style={{ padding: '12px 15px', textAlign: 'right' }}>₹{parseFloat(line.payment_amount || 0).toFixed(2)}</td>
                              <td style={{ padding: '12px 15px', textAlign: 'center' }}>{line.payment_type}</td>
                              {!isPaymentReadOnly && (
                                <td style={{ padding: '12px 15px', textAlign: 'center' }}>
                                  <button
                                    type="button"
                                    className="btn-icon btn-delete"
                                    onClick={() => handleDeletePaymentLine(line)}
                                    title="Delete Payment Line"
                                  >
                                    🗑️
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p style={{ textAlign: 'center', color: '#718096', padding: '20px', fontStyle: 'italic' }}>
                      No payment lines yet. Add a payment above to get started.
                    </p>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                {!isPaymentReadOnly && (
                  <button type="submit" className="btn-primary">
                    Add Payment
                  </button>
                )}
                <button type="button" className="btn-secondary" onClick={handleClosePaymentModal}>
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Record History Modal */}
      {historyItem && (
        <div className="modal-overlay" onClick={() => setHistoryItem(null)}>
          <div className="modal-content" style={{ maxWidth: 560, maxHeight: '75vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Record History — {historyItem.order_no || historyItem.name}</h3>
              <button className="close-button" onClick={() => setHistoryItem(null)}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '16px', overflowY: 'auto' }}>
              {historyLoading && <p style={{ color: '#999' }}>Loading…</p>}
              {historyData?.error && <p style={{ color: '#c33' }}>{historyData.error}</p>}
              {historyData && !historyData.error && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', padding: '12px 14px', background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 6, fontSize: '0.85rem' }}>
                  <div><span style={{ color: '#888' }}>Created</span><br /><strong>{historyData.audit.created_date ? new Date(historyData.audit.created_date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false }) : '—'}</strong></div>
                  <div><span style={{ color: '#888' }}>Created By</span><br /><strong>{historyData.audit.created_by || '—'}</strong></div>
                  <div><span style={{ color: '#888' }}>Last Updated</span><br /><strong>{historyData.audit.updated_date ? new Date(historyData.audit.updated_date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false }) : '—'}</strong></div>
                  <div><span style={{ color: '#888' }}>Updated By</span><br /><strong>{historyData.audit.updated_by || '—'}</strong></div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setHistoryItem(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrders;
